import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import ReactTable from 'react-table';
import { Eye } from 'styled-icons/feather/Eye';
import { useHistory } from 'react-router-dom';

import { COLORS, Separator, Col } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';

import {
  getTeacherClassroom,
  getTeacherClassroomSummary,
} from '../../api/regular-class-analytics';
import {
  formatClassDate,
  formatSeconds,
  safeSessionGet,
  mergeClassroomSummary,
} from '../../utils/classroom-helpers';
import { trackEvent } from 'modules/analytics';
import { EVENT_TYPE } from 'constants/logger';

const TableWrapper = styled.div`
  width: 100%;
  margin: 0 auto;
  background: ${COLORS.WHITE};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
  overflow-y: hidden;

  .rt-table {
    border: none;
  }

  .rt-th,
  .rt-td {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
  }
`;

const SearchWrapper = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  align-items: center;
`;

const SearchInputWrapper = styled.div`
  display: flex;
  align-items: center;
  background: ${COLORS.WHITE};
  border: 1px solid ${COLORS.GRAY_LIGHT};
  border-radius: 8px;
  padding: 0 12px;
  min-height: 38px;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${COLORS.COLA};
  }

  &:focus-within {
    border-color: ${COLORS.COLA};
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }
`;

const SearchLabel = styled.span`
  font-size: 14px;
  color: ${COLORS.COLA};
  white-space: nowrap;
`;

const FilterDivider = styled.div`
  width: 1px;
  height: 18px;
  background: ${COLORS.GRAY_LIGHT};
  margin: 0 10px;
  flex-shrink: 0;
`;

const FilterInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  width: 320px;

  &::placeholder {
    color: ${COLORS.GRAY};
  }
`;

const EyeIcon = styled(Eye)`
  color: ${COLORS.DARK};
  cursor: pointer;
  width: 20px;
  height: 20px;
  stroke-width: 2;
  &:hover {
    color: ${COLORS.BLACK};
  }
`;

const DisabledEyeIcon = styled(Eye)`
  color: ${COLORS.GRAY_LIGHT};
  width: 20px;
  height: 20px;
  stroke-width: 2;
  cursor: not-allowed;
`;

const ActionCell = styled(Col)`
  && {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const NoRecordingCell = styled(Col)`
  && {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
  }
`;

const GradeBold = styled.span`
  font-weight: 500;
`;

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${COLORS.PALE_GREY};
  color: ${COLORS.DARK};
`;

const NoRecordingText = styled.span`
  font-size: 11px;
  color: ${COLORS.GRAY};
  font-style: italic;
`;

const SearchPromptWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: ${COLORS.WHITE};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const REGULAR_CLASS_STATE_KEY = 'regular_class_dashboard_state';

function RegularClassTable() {
  const history = useHistory();

  const restoredState = safeSessionGet(REGULAR_CLASS_STATE_KEY);

  const [searchValue, setSearchValue] = useState(restoredState?.searchValue || '');
  const [classroom, setClassroom] = useState(restoredState?.classroom || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (restoredState) {
      sessionStorage.removeItem(REGULAR_CLASS_STATE_KEY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = useCallback(async () => {
    const trimmed = searchValue.trim();
    if (!trimmed) {
      setClassroom(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setClassroom(null);

    trackEvent(EVENT_TYPE.CLASSROOM_SEARCH, {
      teacher_classroom_id: trimmed,
    });

    try {
      const [classroomRes, summaryRes] = await Promise.all([
        getTeacherClassroom(trimmed),
        getTeacherClassroomSummary(trimmed),
      ]);

      const merged = mergeClassroomSummary(classroomRes, summaryRes, trimmed);
      setClassroom(merged);
    } catch (err) {
      setError('Classroom not found. Please check the ID and try again.');
    } finally {
      setLoading(false);
    }
  }, [searchValue]);

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      handleSearch();
    } else if (e.key === 'Escape') {
      setSearchValue('');
      setClassroom(null);
      setError(null);
    }
  };

  const handleViewClick = useCallback(
    row => {
      const teacherClassroomId = row.teacher_classroom_id;

      trackEvent(EVENT_TYPE.CLASSROOM_VIEW_CLICKED, {
        teacher_classroom_id: teacherClassroomId,
        should_record_bool: row.should_record_bool,
      });

      sessionStorage.setItem(
        REGULAR_CLASS_STATE_KEY,
        JSON.stringify({ searchValue: teacherClassroomId, classroom: row }),
      );
      history.push(`/classroom-view/${teacherClassroomId}`, { classroom: row });
    },
    [history],
  );

  const tableData = classroom ? [classroom] : [];

  const tableColumns = [
    {
      Header: 'Regular Classes',
      columns: [
        {
          Header: 'Actions',
          accessor: 'actions',
          width: 80,
          Cell: ({ original }) => {
            const shouldRecord = original.should_record_bool;
            if (!shouldRecord) {
              trackEvent(EVENT_TYPE.CLASSROOM_NO_RECORDING, {
                teacher_classroom_id: original.teacher_classroom_id,
              });
              return (
                <NoRecordingCell>
                  <DisabledEyeIcon />
                  <NoRecordingText>No recording</NoRecordingText>
                </NoRecordingCell>
              );
            }
            return (
              <ActionCell>
                <EyeIcon onClick={() => handleViewClick(original)} />
              </ActionCell>
            );
          },
        },
        {
          Header: 'Teacher Name',
          accessor: 'teacher_name',
          width: 150,
          Cell: ({ value }) => value || '-',
        },
        {
          Header: 'Student Name',
          accessor: 'student_name',
          width: 180,
          Cell: ({ value }) => value || '-',
        },
        {
          Header: 'Grade',
          accessor: 'student_grade',
          width: 80,
          Cell: ({ value }) => <GradeBold>{value || '-'}</GradeBold>,
        },
        {
          Header: 'Date',
          accessor: 'scheduled_start_datetime',
          width: 120,
          Cell: ({ value }) => formatClassDate(value),
        },
        {
          Header: 'Duration',
          accessor: 'call_duration',
          width: 100,
          Cell: ({ value }) => formatSeconds(value),
        },
        {
          Header: 'Status',
          accessor: 'status',
          width: 80,
          Cell: ({ value }) => <StatusBadge>{value || '-'}</StatusBadge>,
        },
      ],
    },
  ];

  return (
    <>
      <SearchWrapper>
        <SearchInputWrapper>
          <SearchLabel>Classroom ID</SearchLabel>
          <FilterDivider />
          <FilterInput
            type="text"
            placeholder="Enter teacher_classroom_id and press Enter"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </SearchInputWrapper>
      </SearchWrapper>

      {!classroom && !loading && !error && (
        <SearchPromptWrapper>
          <Text variant="h5">Search for a classroom</Text>
          <Separator height={8} transparent />
          <Text variant="body2Regular" color={COLORS.GRAY}>
            Enter a teacher_classroom_id to view class details and recordings.
          </Text>
        </SearchPromptWrapper>
      )}

      {error && (
        <SearchPromptWrapper>
          <Text variant="body1Regular" color="red">
            {error}
          </Text>
        </SearchPromptWrapper>
      )}

      {loading && (
        <SearchPromptWrapper>
          <Text variant="body1Regular">Loading...</Text>
        </SearchPromptWrapper>
      )}

      {classroom && !loading && (
        <TableWrapper>
          <ReactTable
            data={tableData}
            columns={tableColumns}
            showPagination={false}
            minRows={1}
            sortable={false}
          />
        </TableWrapper>
      )}
    </>
  );
}

export default RegularClassTable;
